import { useCallback, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useIntl } from "react-intl";

import TimeInput from "components/v2/TimeInput/TimeInput";
import { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useRequestFormSaveOrNull } from "@RequestFormV2/context/save";
import { useReactiveReviewClear } from "@RequestFormV2/hooks/useReactiveReviewClear";
import { useSyncedClearErrors } from "@RequestFormV2/hooks/useSyncedClearErrors";
import {
  getStopsWithLatestHourValue,
  isStopHoursOrderValid,
  validateHoursOrder,
} from "@RequestFormV2/sections/Schedule/validation/validateSchedule";
import { StopPlanningSlice } from "@RequestFormV2/sections/Schedule/hooks/useStopPlanning";
import StopPlanningToggleGroup from "@RequestFormV2/sections/Schedule/components/StopCard/ScheduleSection/StopPlanningToggleGroup";

interface StopAvailableHoursProps {
  planning: StopPlanningSlice;
  stopIndex: number;
}

export default function StopAvailableHours({ planning, stopIndex }: StopAvailableHoursProps) {
  const intl = useIntl();
  const { setError, getValues } = useFormContext();
  const saveContext = useRequestFormSaveOrNull();
  const syncedClear = useSyncedClearErrors();
  const { showHour, handleShowHour, hoursFromKey, hoursToKey } = planning;
  const hoursToRef = useRef<HTMLInputElement>(null);
  const stops = useWatch({ name: "schedule.stops" });

  const runHoursValidation = useCallback(
    (fieldPath: string, time?: string) => {
      const latestStops = getStopsWithLatestHourValue(
        (getValues("schedule.stops") as ScheduleStopValue[]) ?? [],
        fieldPath,
        time,
      );
      validateHoursOrder(latestStops, intl, setError, syncedClear);
    },
    [getValues, intl, setError, syncedClear],
  );

  const handleHoursTimeSelect = useCallback(
    (fieldPath: string) => (time?: string) => {
      runHoursValidation(fieldPath, time);
      saveContext?.saveField(fieldPath, getValues(fieldPath)).catch(() => undefined);
    },
    [getValues, runHoursValidation, saveContext],
  );

  useReactiveReviewClear(
    hoursToKey,
    () => {
      const stop = (getValues("schedule.stops") as ScheduleStopValue[] | undefined)?.[stopIndex];
      return stop ? isStopHoursOrderValid(stop) : true;
    },
    [stops, stopIndex],
    showHour,
  );

  return (
    <StopPlanningToggleGroup
      isVisible={showHour}
      addLabel={intl.formatMessage({
        id: "request-form.schedule.planning.addAvailableHours",
        defaultMessage: "Add available hours",
      })}
      removeLabel={intl.formatMessage({
        id: "request-form.schedule.planning.removeAvailableHours",
        defaultMessage: "Remove available hours",
      })}
      onToggle={handleShowHour}
    >
      <div className="rfv2-three-column-grid">
        <div className="rfv2-schedule-hours">
          <TimeInput
            name={hoursFromKey}
            label={intl.formatMessage({
              id: "request-form.schedule.planning.hoursStart",
              defaultMessage: "Hours from",
            })}
            skipTriggerOnComplete
            onComplete={() => hoursToRef.current?.focus()}
            onTimeSelect={handleHoursTimeSelect(hoursFromKey)}
          />
          <TimeInput
            name={hoursToKey}
            label={intl.formatMessage({
              id: "request-form.schedule.planning.hoursEnd",
              defaultMessage: "Hours to",
            })}
            skipTriggerOnComplete
            inputRef={hoursToRef}
            onTimeSelect={handleHoursTimeSelect(hoursToKey)}
          />
        </div>
      </div>
    </StopPlanningToggleGroup>
  );
}
